/**
 * Rig de camera orthographique "diorama" (azimut et elevation de 45 degres)
 * et outils de projection ecran -> monde.
 *
 * La camera ne tourne jamais autour de l'axe horizontal: seuls l'azimut (libre)
 * et le zoom (demi-hauteur du frustum) changent. En orthographique la distance
 * camera-cible n'a aucun effet sur la taille apparente: on la garde grande pour
 * que rien ne soit coupe, meme une montagne devant la camera.
 *
 *   const rig = new RigCamera(innerWidth, innerHeight)
 *   rig.suivre(joueur.x, joueur.y, joueur.z, dt)
 *   rig.maj(dt)
 *   rendu.render(scene, rig.camera)
 */
import * as THREE from 'three'
import {
  CAM_AZIMUTH,
  CAM_ELEVATION,
  CAM_ZOOM_DEFAULT,
  CAM_ZOOM_INTERIEUR,
  CAM_ZOOM_MAX,
  CAM_ZOOM_MAX_VOL,
  CAM_ZOOM_MIN,
  SEA_LEVEL,
  CARTE_X,
  MER_OUEST,
  CARTE_Z,
  WORLD_SIZE,
} from '../../shared/constants.ts'
import { clamp } from '../../shared/sim/alea.ts'
import type { ITerrain, Vec2 } from '../../shared/types/world.ts'

/** Distance camera-cible en metres. Sans effet sur la taille (orthographique). */
const DISTANCE = 150

/** Plans de coupe genereux: le monde entier peut passer devant la camera. */
const NEAR = 0.5
const FAR = 900

/**
 * Demi-hauteur du frustum en vol, en metres.
 *
 * Trois fois le cadrage ordinaire: a vingt metres par seconde, on voit venir
 * ce vers quoi on vole pendant deux bonnes secondes, ce qui est le minimum
 * pour choisir ou l'on va.
 */
const CAM_ZOOM_VOL = 26

/** Raideurs des lissages exponentiels, en 1/s. */
const K_SUIVI = 9
const K_ZOOM = 12
const K_PIVOT = 9

/** Au-dela de cette distance, la camera se recale d'un coup (apparition, teleport). */
const SAUT_SUIVI = 25

/** Angle ramene dans (-PI, PI]. */
function angleCourt(a: number): number {
  let x = a % (Math.PI * 2)
  if (x > Math.PI) x -= Math.PI * 2
  if (x <= -Math.PI) x += Math.PI * 2
  return x
}

export class RigCamera {
  readonly camera: THREE.OrthographicCamera
  /** Point regarde (position du joueur). Le frustum d'ombre suit ce point. */
  readonly cible: THREE.Vector3

  private largeur: number
  private hauteur: number

  private azimutCourant = CAM_AZIMUTH
  private azimutVise = CAM_AZIMUTH
  private pivotAnime = false

  private zoomCourant = CAM_ZOOM_DEFAULT
  private zoomVise = CAM_ZOOM_DEFAULT
  /** Zoom memorise avant d'entrer dans un batiment. */
  private zoomExterieur = CAM_ZOOM_DEFAULT
  /** Vrai quand la camera est en vue de vol (voir `vueDeVol`). */
  private enVol = false
  /** Cadrage d'avant le decollage, rendu a l'atterrissage. */
  private zoomAvantVol = CAM_ZOOM_DEFAULT
  private dedans = false

  /** Vecteurs de travail: aucune allocation dans les boucles chaudes. */
  private readonly tmp = new THREE.Vector3()

  constructor(largeur: number, hauteur: number) {
    this.largeur = Math.max(1, largeur)
    this.hauteur = Math.max(1, hauteur)
    this.cible = new THREE.Vector3(WORLD_SIZE / 2, 0, WORLD_SIZE / 2)

    const aspect = this.largeur / this.hauteur
    this.camera = new THREE.OrthographicCamera(
      -CAM_ZOOM_DEFAULT * aspect,
      CAM_ZOOM_DEFAULT * aspect,
      CAM_ZOOM_DEFAULT,
      -CAM_ZOOM_DEFAULT,
      NEAR,
      FAR,
    )
    this.camera.up.set(0, 1, 0)
    this.appliquerProjection()
    this.placer()
  }

  // ------------------------------------------------------------ lectures

  get azimut(): number {
    return this.azimutCourant
  }

  get zoom(): number {
    return this.zoomCourant
  }

  get elevation(): number {
    return CAM_ELEVATION
  }

  /** Vrai quand la camera est en mode interieur. */
  get interieur(): boolean {
    return this.dedans
  }

  // ------------------------------------------------------------ commandes

  /**
   * Suit la cible avec un amortissement independant du framerate.
   * A appeler avant maj() chaque frame.
   */
  suivre(x: number, y: number, z: number, dt: number): void {
    const dx = x - this.cible.x
    const dy = y - this.cible.y
    const dz = z - this.cible.z
    if (dx * dx + dy * dy + dz * dz > SAUT_SUIVI * SAUT_SUIVI) {
      this.cible.set(x, y, z)
    } else {
      const a = 1 - Math.exp(-K_SUIVI * Math.max(0, dt))
      this.cible.x += dx * a
      this.cible.y += dy * a
      this.cible.z += dz * a
    }
    this.placer()
  }

  redimensionner(largeur: number, hauteur: number): void {
    this.largeur = Math.max(1, largeur)
    this.hauteur = Math.max(1, hauteur)
    this.appliquerProjection()
  }

  /**
   * Zoom multiplicatif: facteur > 1 eloigne, facteur < 1 rapproche.
   *
   * Le plafond n'est pas le meme a pied et en l'air (`CAM_ZOOM_MAX_VOL`): on
   * pilote un avion en DESIGNANT un endroit du monde, donc il faut pouvoir
   * reculer jusqu'a le voir. Le plancher, lui, ne bouge pas.
   */
  zoomer(facteur: number): void {
    if (!Number.isFinite(facteur) || facteur <= 0) return
    this.zoomVise = clamp(this.zoomVise * facteur, CAM_ZOOM_MIN, this.plafondZoom())
    if (!this.dedans) this.zoomExterieur = this.zoomVise
  }

  /** Le plafond du zoom ici et maintenant: le vol seul l'ouvre en grand. */
  private plafondZoom(): number {
    return this.enVol ? CAM_ZOOM_MAX_VOL : CAM_ZOOM_MAX
  }

  /** Rotation libre autour de la cible (geste en cours: reponse immediate). */
  pivoter(deltaRadians: number): void {
    if (!Number.isFinite(deltaRadians) || deltaRadians === 0) return
    this.azimutCourant += deltaRadians
    this.azimutVise = this.azimutCourant
    this.pivotAnime = false
    this.placer()
  }

  /** Rotation animee vers un azimut donne (bouton "aligner"). */
  pivoterVers(azimut: number): void {
    if (!Number.isFinite(azimut)) return
    // Viser l'equivalent le plus proche pour tourner du bon cote.
    this.azimutVise = this.azimutCourant + angleCourt(azimut - this.azimutCourant)
    this.pivotAnime = true
  }

  /** Pivote vers le multiple de 90 degres le plus proche. */
  alignerSur90(): void {
    const quart = Math.PI / 2
    this.pivoterVers(Math.round(this.azimutCourant / quart) * quart)
  }

  /**
   * LA VUE DE VOL: on s'ecarte, parce qu'un avion va cinq fois plus vite qu'un
   * bonhomme.
   *
   * Au cadrage ordinaire (9 m de demi-hauteur), vingt metres par seconde
   * traversent l'ecran en moins d'une seconde: on ne voit plus ou l'on va, et
   * l'appareil semble fuir devant la camera. On prend donc de la hauteur des
   * que les roues quittent le sol, et l'on retrouve son cadrage en se posant —
   * le meme marche que `entrerInterieur`, qui range le zoom du dehors pour le
   * rendre en sortant.
   */
  vueDeVol(actif: boolean): void {
    if (actif === this.enVol) return
    this.enVol = actif
    if (actif) {
      this.zoomAvantVol = this.zoomVise
      this.zoomVise = CAM_ZOOM_VOL
    } else {
      // On revient au cadrage d'avant le decollage, et sous le plafond du
      // marcheur: le grand ecart de la vue de vol ne survit pas a l'appareil.
      this.zoomVise = clamp(this.zoomAvantVol, CAM_ZOOM_MIN, CAM_ZOOM_MAX)
      this.zoomExterieur = clamp(this.zoomExterieur, CAM_ZOOM_MIN, CAM_ZOOM_MAX)
    }
  }

  entrerInterieur(): void {
    if (this.dedans) return
    this.dedans = true
    this.zoomExterieur = this.zoomVise
    this.zoomVise = CAM_ZOOM_INTERIEUR
  }

  sortirInterieur(): void {
    if (!this.dedans) return
    this.dedans = false
    this.zoomVise = clamp(this.zoomExterieur, CAM_ZOOM_MIN, CAM_ZOOM_MAX)
  }

  /** Fait avancer les animations (pivot, zoom) et recalcule les matrices. */
  maj(dt: number): void {
    const pas = Math.max(0, dt)

    if (this.zoomCourant !== this.zoomVise) {
      const ecart = this.zoomVise - this.zoomCourant
      if (Math.abs(ecart) < 1e-3) this.zoomCourant = this.zoomVise
      else this.zoomCourant += ecart * (1 - Math.exp(-K_ZOOM * pas))
      this.appliquerProjection()
    }

    if (this.pivotAnime) {
      const ecart = this.azimutVise - this.azimutCourant
      if (Math.abs(ecart) < 1e-4) {
        this.azimutCourant = this.azimutVise
        this.pivotAnime = false
      } else {
        this.azimutCourant += ecart * (1 - Math.exp(-K_PIVOT * pas))
      }
    }

    this.placer()
  }

  // ------------------------------------------------------------ interne

  private appliquerProjection(): void {
    const aspect = this.largeur / this.hauteur
    this.camera.left = -this.zoomCourant * aspect
    this.camera.right = this.zoomCourant * aspect
    this.camera.top = this.zoomCourant
    this.camera.bottom = -this.zoomCourant
    this.camera.updateProjectionMatrix()
  }

  private placer(): void {
    const ce = Math.cos(CAM_ELEVATION)
    const se = Math.sin(CAM_ELEVATION)
    this.tmp.set(
      Math.cos(this.azimutCourant) * ce * DISTANCE,
      se * DISTANCE,
      Math.sin(this.azimutCourant) * ce * DISTANCE,
    )
    this.camera.position.copy(this.cible).add(this.tmp)
    this.camera.lookAt(this.cible)
    this.camera.updateMatrixWorld()
  }
}

// ------------------------------------------------------------------ picking

/**
 * Rayon monde correspondant a un point de l'ecran.
 * En orthographique tous les rayons sont paralleles: seule l'origine change.
 */
export function rayonDepuisEcran(
  camera: THREE.OrthographicCamera,
  clientX: number,
  clientY: number,
  largeur: number,
  hauteur: number,
): THREE.Ray {
  const nx = (clientX / Math.max(1, largeur)) * 2 - 1
  const ny = -((clientY / Math.max(1, hauteur)) * 2 - 1)
  camera.updateMatrixWorld()
  // z = -1: point sur le plan rapproche du frustum.
  const origine = new THREE.Vector3(nx, ny, -1).unproject(camera)
  const direction = new THREE.Vector3(0, 0, -1).transformDirection(camera.matrixWorld)
  return new THREE.Ray(origine, direction)
}

/**
 * Le point ou le rayon traverse un PLAN horizontal, ou null.
 *
 * `pointSol` marche le long du relief, et c'est tout ce qu'il sait faire: un
 * tap ne peut donc jamais viser nativement une surface surelevee. Quand le
 * joueur est sur une terrasse, ce n'est plus le relief qui compte mais la dalle
 * sur laquelle il se tient, et une dalle est exactement un plan y = constante.
 * L'intersection est alors exacte et coute trois multiplications, la ou le
 * relief demande une marche et une bissection.
 *
 * L'appelant est responsable de verifier que le point tombe bien SUR la
 * terrasse: hors de son emprise, il n'y a pas de plancher (voir `surTap` dans
 * client/jeu.ts).
 */
export function pointPlan(rayon: THREE.Ray, y: number): Vec2 | null {
  const dy = rayon.direction.y
  if (Math.abs(dy) < 1e-9) return null
  const t = (y - rayon.origin.y) / dy
  if (t < 0) return null
  return { x: rayon.origin.x + rayon.direction.x * t, z: rayon.origin.z + rayon.direction.z * t }
}

/** Pas de marche le long du rayon, en metres. */
const PAS_MARCHE = 0.5

/** Iterations de bissection: 0.5 / 2^8 = 2 mm. */
const ITER_BISSECTION = 8

/**
 * Premiere intersection du rayon avec le sol.
 *
 * On borne d'abord le rayon a la boite du monde (evite de sonder le terrain
 * hors limites), puis on marche par pas de 0.5 m jusqu'a passer sous la surface
 * et on affine par bissection. Renvoie null si le rayon ne touche pas le sol
 * dans le monde.
 */
export function pointSol(rayon: THREE.Ray, terrain: ITerrain, maxDistance = 500): Vec2 | null {
  const ox = rayon.origin.x
  const oy = rayon.origin.y
  const oz = rayon.origin.z
  const dx = rayon.direction.x
  const dy = rayon.direction.y
  const dz = rayon.direction.z

  // Un rayon qui monte ne rencontrera jamais le sol.
  if (dy >= 0) return null

  // Decoupe par la boite du monde sur x et z.
  let t0 = 0
  let t1 = maxDistance
  if (Math.abs(dx) < 1e-9) {
    if (ox < 0 || ox > CARTE_X) return null
  } else {
    let a = (0 - ox) / dx
    let b = (CARTE_X - ox) / dx
    if (a > b) {
      const s = a
      a = b
      b = s
    }
    if (a > t0) t0 = a
    if (b < t1) t1 = b
  }
  if (Math.abs(dz) < 1e-9) {
    if (oz < 0 || oz > CARTE_Z) return null
  } else {
    let a = (0 - oz) / dz
    let b = (CARTE_Z - oz) / dz
    if (a > b) {
      const s = a
      a = b
      b = s
    }
    if (a > t0) t0 = a
    if (b < t1) t1 = b
  }
  if (t1 <= t0) return null

  // f(t) > 0 tant qu'on est au-dessus du terrain.
  const f = (t: number): number => oy + dy * t - terrain.hauteur(ox + dx * t, oz + dz * t)

  let tHaut = t0
  if (f(tHaut) <= 0) return { x: ox + dx * tHaut, z: oz + dz * tHaut }

  let tBas = -1
  for (let t = t0 + PAS_MARCHE; ; t += PAS_MARCHE) {
    const borne = t > t1
    const tt = borne ? t1 : t
    if (f(tt) <= 0) {
      tBas = tt
      break
    }
    tHaut = tt
    if (borne) break
  }
  if (tBas < 0) return null

  for (let i = 0; i < ITER_BISSECTION; i++) {
    const tm = (tHaut + tBas) * 0.5
    if (f(tm) > 0) tHaut = tm
    else tBas = tm
  }

  const t = (tHaut + tBas) * 0.5
  const x = ox + dx * t
  const z = oz + dz * t
  if (x < 0 || z < 0 || x > CARTE_X || z > CARTE_Z) return null
  return { x, z }
}

/**
 * OU LE DOIGT TOMBE VRAIMENT: la premiere SURFACE rencontree, eau comprise.
 *
 * `pointSol` marche le long du relief, et sous la mer le relief est le FOND.
 * Viser une barque au large renvoyait donc un point du fond marin, situe aussi
 * loin derriere elle que l'eau est profonde: un rayon qui descend a 45 degres
 * doit avancer d'un metre de plus par metre de profondeur. A dix metres de
 * fond, le tap tombait dix metres au-dela de la coque — plus de six fois la
 * tolerance de visee. La barque n'etait donc PAS TOUCHABLE des qu'elle
 * flottait ailleurs qu'a deux pas de la rive, et le journal du 7 aout en garde
 * la trace: dix taps autour d'une barque, pas un embarquement.
 *
 * C'est le meme aveuglement que pour la terrasse, et il se repare de la meme
 * facon: une surface plane se coupe exactement (`pointPlan`), le relief se
 * marche. Reste a savoir laquelle des deux le rayon atteint EN PREMIER.
 *
 * La premiere ecriture croyait n'avoir rien a arbitrer: « le fond est sous la
 * mer, donc le rayon traverse la nappe avant le fond ». C'est vrai du fond
 * SOUS le point de la nappe, pas du relief AVANT lui: une dune au bord de l'eau
 * arrete le rayon bien avant qu'il redescende au niveau de la mer derriere
 * elle, et le tap sur la dune partait dans l'eau. On marche donc le relief
 * jusqu'a la nappe, pas plus loin: s'il est touche d'abord, c'est lui.
 *
 * Sur la terre ferme, la nappe est sous le sol: on retombe sur `pointSol`, et
 * rien de ce qui existait ne change.
 */
export function pointSurface(rayon: THREE.Ray, terrain: ITerrain, maxDistance = 500): Vec2 | null {
  const mer = pointPlan(rayon, SEA_LEVEL)
  if (
    mer &&
    mer.x >= -MER_OUEST &&
    mer.z >= 0 &&
    mer.x <= CARTE_X &&
    mer.z <= CARTE_Z &&
    terrain.estEau(mer.x, mer.z)
  ) {
    // Distance le long du rayon jusqu'a la nappe (`direction` est unitaire).
    const tMer = (SEA_LEVEL - rayon.origin.y) / rayon.direction.y
    return pointSol(rayon, terrain, Math.min(tMer, maxDistance)) ?? mer
  }
  return pointSol(rayon, terrain, maxDistance)
}
